import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Card } from '../components/Card';
import { useAuth } from '../context/AuthContext';
import { validateEmail } from '../utils/validation';
import { sendAdminOtp, verifyAdminOtp } from '../services/api';
import { ROUTES } from '../constants';

const OTP_EXPIRY_SECONDS = 300;

export const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const { adminLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    otp: '',
  });

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const validateEmailField = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
      setErrors(newErrors);
      return false;
    }

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const validateOtpField = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.otp) {
      newErrors.otp = 'OTP is required';
      setErrors(newErrors);
      return false;
    }

    if (formData.otp.length !== 6) {
      newErrors.otp = 'OTP must be 6 digits';
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmailField()) {
      return;
    }

    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    const result = await sendAdminOtp({ email: formData.email });

    setLoading(false);

    if (result.success) {
      setOtpSent(true);
      setCountdown(OTP_EXPIRY_SECONDS);
      setSuccessMessage('OTP sent to your email successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
    } else {
      setErrors({ submit: result.message });
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateOtpField()) {
      return;
    }

    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    const result = await verifyAdminOtp({
      email: formData.email,
      otp: formData.otp,
    });

    setLoading(false);

    if (result.success) {
      if (adminLogin) {
        await adminLogin();
      }
      navigate(ROUTES.ADMIN_DASHBOARD);
    } else {
      setErrors({ submit: result.message });
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center p-3 sm:p-4">
      <Card className="w-full max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <Logo size="lg" className="justify-center mb-4 sm:mb-6" />
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Admin Access</h1>
          <p className="text-sm sm:text-base text-gray-600">Secure OTP authentication</p>
        </div>

        {errors.submit && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs sm:text-sm flex items-start sm:items-center">
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0 mt-0.5 sm:mt-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="break-words">{errors.submit}</span>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg text-green-600 text-xs sm:text-sm flex items-start sm:items-center">
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0 mt-0.5 sm:mt-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="break-words">{successMessage}</span>
          </div>
        )}

        {!otpSent ? (
          <form onSubmit={handleSendOtp} className="space-y-4 sm:space-y-5">
            <Input
              label="Admin Email Address"
              type="email"
              placeholder="Enter admin email"
              icon={Mail}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={errors.email}
              disabled={loading}
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send OTP'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-4 sm:space-y-5">
            <Input
              label="Email Address"
              type="email"
              icon={Mail}
              value={formData.email}
              disabled={true}
            />

            <div>
              <Input
                label="Enter OTP"
                type="text"
                placeholder="Enter 6-digit OTP"
                icon={Lock}
                value={formData.otp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setFormData({ ...formData, otp: value });
                }}
                error={errors.otp}
                disabled={loading}
                maxLength={6}
              />
              {countdown > 0 && (
                <p className="text-sm text-gray-600 mt-2">
                  OTP expires in: <span className="font-semibold text-blue-600">{formatTime(countdown)}</span>
                </p>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={loading || countdown === 0}
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setOtpSent(false);
                  setFormData({ ...formData, otp: '' });
                  setCountdown(0);
                  setErrors({});
                  setSuccessMessage('');
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                disabled={loading}
              >
                Resend OTP
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => navigate(ROUTES.LOGIN)}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Back to Login
          </button>
        </div>
      </Card>
    </div>
  );
};
